import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import FullCalendar from '@fullcalendar/react';
import Switch from '@mui/material/Switch';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/themes/light.css';
import { extraerAgenda } from '../../../../store/agenda/thunkAgenda';

/**
 * @description Componente para visualizar la agenda con múltiples filtros y vistas
 * @returns {JSX.Element} Componente AgendaVer
 */
const AgendaVer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const calendarRef = useRef(null);

  // Estados para controlar la UI
  const [cargando, setCargando] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [currentView, setCurrentView] = useState(window.innerWidth < 768 ? 'listWeek' : 'dayGridMonth');

  // Estados para datos
  const [eventosAPI, setEventosAPI] = useState([]);

  // Estados para filtros
  const [filtros, setFiltros] = useState({});
  const [filtrosVendedores, setFiltrosVendedores] = useState({});
  const [filtrosInicializados, setFiltrosInicializados] = useState(false);
  const [filtrosVendedoresInicializados, setFiltrosVendedoresInicializados] = useState(false);

  // Estados para fechas de filtro
  const [fechaInicioFiltro, setFechaInicioFiltro] = useState('');
  const [fechaFinFiltro, setFechaFinFiltro] = useState('');

  /**
   * @description Navega a la página de creación de eventos
   */
  const handleCrearEvento = () => navigate('/agenda/crear');

  /**
   * @description Maneja el clic en una fecha del calendario
   * @param {Object} info - Información del evento de clic
   */
  const handleDateClick = (info) => navigate(`/agenda/crear?data=${info.dateStr}`);

  /**
   * @description Maneja el clic en un evento existente
   * @param {Object} info - Información del evento
   */
  const handleEventClick = (info) => navigate(`/agenda/editar?data=${info.event.id}`);

  /**
   * @description Cambia la vista del calendario
   * @param {string} viewName - Nombre de la vista a mostrar
   */
  const handleViewChange = (viewName) => {
    setCurrentView(viewName);
    if (calendarRef.current) {
      calendarRef.current.getApi().changeView(viewName);
    }
  };

  /**
   * @description Cambia el estado de un filtro por tipo
   * @param {string} tipo - Tipo de evento a filtrar
   */
  const handleFiltroChange = (tipo) => {
    setFiltros(prevFiltros => ({
      ...prevFiltros,
      [tipo]: !prevFiltros[tipo]
    }));
  };

  /**
   * @description Cambia el estado de un filtro por vendedor
   * @param {string} vendedor - Vendedor a filtrar
   */
  const handleFiltroVendedorChange = (vendedor) => {
    setFiltrosVendedores(prevFiltros => ({
      ...prevFiltros,
      [vendedor]: !prevFiltros[vendedor]
    }));
  };

  /**
   * @description Aplica los filtros de fecha seleccionados
   */
  const aplicarFiltrosFecha = async () => {
    try {
      // Validar que ambas fechas estén presentes
      if (!fechaInicioFiltro || !fechaFinFiltro) {
        Swal.fire({
          title: 'Error',
          text: 'Debes seleccionar ambas fechas',
          icon: 'warning',
          confirmButtonText: 'Aceptar'
        });
        return;
      }

      // Validar que la fecha de inicio sea menor que la fecha de fin
      if (new Date(fechaInicioFiltro) > new Date(fechaFinFiltro)) {
        Swal.fire({
          title: 'Error',
          text: 'La fecha de inicio debe ser anterior a la fecha de fin',
          icon: 'warning',
          confirmButtonText: 'Aceptar'
        });
        return;
      }

      // Mostrar indicador de carga
      setCargando(true);
      Swal.fire({
        title: 'Filtrando eventos',
        html: 'Por favor espere mientras actualizamos su agenda...',
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => Swal.showLoading()
      });

      // Obtener datos filtrados por fecha
      const result = await dispatch(extraerAgenda('pendiente', fechaInicioFiltro, fechaFinFiltro));
      setEventosAPI(result.data.data || []);

      // Cerrar indicador de carga
      Swal.close();
      setCargando(false);

      // Notificar éxito
      Swal.fire({
        title: 'Filtrado completado',
        text: 'Se han actualizado los eventos según el rango de fechas seleccionado',
        icon: 'success',
        confirmButtonText: 'Aceptar'
      });
    } catch (error) {
      console.error('Error al filtrar la agenda:', error);
      Swal.close();
      setCargando(false);

      Swal.fire({
        title: 'Error',
        text: 'No se pudieron filtrar los eventos de la agenda',
        icon: 'error',
        confirmButtonText: 'Aceptar'
      });
    }
  };

  // Detectar cambios de tamaño de la ventana
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);

      if (mobile && calendarRef.current) {
        setCurrentView('listWeek');
        calendarRef.current.getApi().changeView('listWeek');
      } else if (!mobile && currentView === 'listWeek' && calendarRef.current) {
        setCurrentView('dayGridMonth');
        calendarRef.current.getApi().changeView('dayGridMonth');
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [currentView]);

  // Cargar datos iniciales
  useEffect(() => {
    const fetchAgenda = async () => {
      try {
        setCargando(true);
        Swal.fire({
          title: 'Cargando eventos',
          html: 'Por favor espere mientras cargamos su agenda...',
          allowOutsideClick: false,
          allowEscapeKey: false,
          didOpen: () => Swal.showLoading()
        });

        // Obtener fechas para el rango inicial
        const hoy = new Date();

        // Establecer fecha de inicio una semana antes
        const fechaInicioTemp = new Date(hoy);
        fechaInicioTemp.setDate(fechaInicioTemp.getDate() - 7); // Resta 7 días
        const fechaInicio = fechaInicioTemp.toISOString().split('T')[0];

        const fechaFin = new Date(hoy);
        fechaFin.setMonth(hoy.getMonth() + 12);
        const fechaFinFormateada = fechaFin.toISOString().split('T')[0];

        // Establecer fechas en los filtros
        setFechaInicioFiltro(fechaInicio);
        setFechaFinFiltro(fechaFinFormateada);

        // Cargar datos
        const result = await dispatch(extraerAgenda('pendiente', fechaInicio, fechaFinFormateada));
        setEventosAPI(result.data.data || []);

        Swal.close();
        setCargando(false);
      } catch (error) {
        console.error('Error al cargar la agenda:', error);
        Swal.close();
        setCargando(false);

        Swal.fire({
          title: 'Error',
          text: 'No se pudieron cargar los eventos de la agenda',
          icon: 'error',
          confirmButtonText: 'Aceptar'
        });
      }
    };

    fetchAgenda();
  }, [dispatch]);

  // Transformar datos para FullCalendar
  const eventos = eventosAPI.map(evento => ({
    id: evento.id_agenda,
    title: evento.nombre_agenda,
    date: evento.fecha_inicio_agenda.split('T')[0],
    start: `${evento.fecha_inicio_agenda.split('T')[0]}T${evento.hora_inicio_agenda}`,
    end: `${evento.fecha_fin_agenda.split('T')[0]}T${evento.hora_fin_agenda}`,
    type: evento.tipo_agenda,
    backgroundColor: evento.color_agenda,
    borderColor: evento.color_agenda,
    description: evento.descripcion_agenda,
    name_admin: evento.name_admin,
    color_agenda: evento.color_agenda,
    nombre_cliente: evento.nombre_cliente,
    nombre_empresa: evento.nombre_empresa,
    estado_agenda: evento.estado_agenda
  }));

  // Extraer tipos y vendedores únicos
  const tipos = [...new Set(eventos.map(e => e.type).filter(Boolean))];
  const vendedores = [...new Set(eventos.map(e => e.name_admin).filter(Boolean))];

  // Inicializar filtros de tipo
  useEffect(() => {
    if (tipos.length > 0 && !filtrosInicializados) {
      const nuevosFiltros = tipos.reduce((acc, tipo) => ({ ...acc, [tipo]: true }), {});
      setFiltros(nuevosFiltros);
      setFiltrosInicializados(true);
    }
  }, [tipos, filtrosInicializados]);

  // Inicializar filtros de vendedores
  useEffect(() => {
    if (vendedores.length > 0 && !filtrosVendedoresInicializados) {
      const nuevosFiltrosVendedores = vendedores.reduce((acc, vendedor) => ({ ...acc, [vendedor]: true }), {});
      setFiltrosVendedores(nuevosFiltrosVendedores);
      setFiltrosVendedoresInicializados(true);
    }
  }, [vendedores, filtrosVendedoresInicializados]);

  // Aplicar filtros combinados
  const eventosFiltrados = eventos.filter(e =>
    (e.type && filtros[e.type] === true) &&
    (!e.name_admin || filtrosVendedores[e.name_admin] === true)
  );

  // Ajustar zoom al cargar
  useEffect(() => {
    const originalZoom = document.body.style.zoom || '100%';

    if ('zoom' in document.body.style) {
      document.body.style.zoom = '80%';
    } else {
      document.documentElement.style.transform = 'scale(0.80)';
      document.documentElement.style.transformOrigin = '0 0';
    }

    return () => {
      if ('zoom' in document.body.style) {
        document.body.style.zoom = originalZoom;
      } else {
        document.documentElement.style.transform = 'none';
        document.documentElement.style.transformOrigin = 'initial';
      }
    };
  }, []);

  // Agregar estilos para el calendario
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      .fc-dayGridWeek-view .fc-daygrid-day {
        height: auto !important;
        min-height: 100px;
      }
      .fc-event-no-padding {
        padding: 0 !important;
        margin: 1px 0 !important;
      }
      .fc-dayGridWeek-view .fc-daygrid-day-events {
        margin-bottom: 0;
        padding: 2px;
        position: relative;
      }
      .fc-dayGridWeek-view .fc-daygrid-day-top {
        justify-content: center;
        padding-top: 5px;
      }
    `;
    document.head.appendChild(styleElement);

    return () => document.head.removeChild(styleElement);
  }, []);

  /**
   * @description Determina el color de texto según el color de fondo
   * @param {string} backgroundColor - Color de fondo en formato hexadecimal
   * @returns {string} Color de texto adecuado (#000000 o #ffffff)
   */
  const getTextColorForBackground = (backgroundColor) => {
    if (!backgroundColor) return '#000000';

    let hex = backgroundColor.replace('#', '');
    if (hex.length === 3) {
      hex = hex.split('').map(h => h + h).join('');
    }

    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    const brightness = (r * 0.299 + g * 0.587 + b * 0.114);
    return brightness > 150 ? '#000000' : '#ffffff';
  };

  return (
    <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '2rem' }}>
      {/* Panel de filtros */}
      <div className={isMobile ? "col-12" : "col-md-6 col-xxl-3"}>
        {/* Filtro por Tipo */}
        <div className="card price-card mb-4">
          <div className="card-body price-head bg-light-secondary">
            <h5 className="text-secondary">Filtrar por Tipo</h5>
            <div className="price-icon bg-light-secondary">
              <i className="fas fa-filter"></i>
            </div>
          </div>
          <div className="card-body">
            <ul className="list-unstyled product-list">
              {tipos.map(tipo => (
                <li className="enable" key={tipo}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.1rem' }}>
                    <Switch
                      checked={filtros[tipo] === true}
                      onChange={() => handleFiltroChange(tipo)}
                      color="primary"
                      sx={{ mr: 1 }}
                    />
                    {tipo}
                  </label>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Filtro por Fecha (NUEVO) */}
        <div className="card price-card mb-4">
          <div className="card-body price-head bg-light-secondary">
            <h5 className="text-secondary">Filtrar por Fecha</h5>
            <div className="price-icon bg-light-secondary">
              <i className="fas fa-calendar-alt"></i>
            </div>
          </div>
          <div className="card-body">
            <div className="mb-3">
              <label htmlFor="fechaInicio" className="form-label">Fecha inicio:</label>
              <input
                type="date"
                className="form-control"
                id="fechaInicio"
                value={fechaInicioFiltro}
                onChange={(e) => setFechaInicioFiltro(e.target.value)}
              />
            </div>
            <div className="mb-3">
              <label htmlFor="fechaFin" className="form-label">Fecha fin:</label>
              <input
                type="date"
                className="form-control"
                id="fechaFin"
                value={fechaFinFiltro}
                onChange={(e) => setFechaFinFiltro(e.target.value)}
              />
            </div>
            <div className="d-grid">
              <button
                className="btn btn-outline-secondary"
                onClick={aplicarFiltrosFecha}
                disabled={cargando}
              >
                {cargando ? 'Aplicando...' : 'Aplicar filtro'}
              </button>
            </div>
          </div>
        </div>

        {/* Filtro por Vendedor */}
        <div className="card price-card">
          <div className="card-body price-head bg-light-secondary">
            <h5 className="text-secondary">Filtrar por Vendedor</h5>
            <div className="price-icon bg-light-secondary">
              <i className="fas fa-users"></i>
            </div>
          </div>
          <div className="card-body">
            <ul className="list-unstyled product-list">
              {vendedores.map(vendedor => (
                <li className="enable" key={vendedor}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.1rem' }}>
                    <Switch
                      checked={filtrosVendedores[vendedor] === true}
                      onChange={() => handleFiltroVendedorChange(vendedor)}
                      color="primary"
                      sx={{ mr: 1 }}
                    />
                    {vendedor}
                  </label>
                </li>
              ))}
            </ul>
            <div className="d-grid mt-3">
              <button
                className="btn btn-outline-secondary"
                onClick={handleCrearEvento}
              >
                Crear un nuevo evento
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Calendario */}
      <div style={{ flex: 1 }}>
        {!isMobile && (
          <div style={{ marginBottom: '1rem' }}>
            <button
              className={`btn ${currentView === 'dayGridMonth' ? 'btn-secondary' : 'btn-outline-secondary'} me-2`}
              onClick={() => handleViewChange('dayGridMonth')}
            >
              Vista Mensual
            </button>
            <button
              className={`btn ${currentView === 'dayGridWeek' ? 'btn-secondary' : 'btn-outline-secondary'} me-2`}
              onClick={() => handleViewChange('dayGridWeek')}
            >
              Vista Semanal
            </button>
            <button
              className={`btn ${currentView === 'listWeek' ? 'btn-secondary' : 'btn-outline-secondary'}`}
              onClick={() => handleViewChange('listWeek')}
            >
              Vista Agenda
            </button>
          </div>
        )}
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, listPlugin, interactionPlugin]}
          initialView={isMobile ? 'listWeek' : 'dayGridMonth'}
          headerToolbar={{
            left: isMobile ? 'prev,next' : 'prev,next today',
            center: 'title',
            right: isMobile ? 'today' : ''
          }}
          events={eventosFiltrados}
          locale="es"
          dateClick={handleDateClick}
          eventClick={handleEventClick}
          selectable={true}
          eventTimeFormat={{
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          }}
          eventDidMount={(info) => {
            const color = info.event.extendedProps.color_agenda || '#cccccc';
            const textColor = getTextColorForBackground(color);
            const eventEl = info.el;

            if (eventEl) {
              // Aplicar estilos visuales
              eventEl.style.backgroundColor = color;
              eventEl.style.borderColor = color;
              eventEl.style.color = textColor;

              const mainContentEl = eventEl.querySelector('.fc-event-main');
              if (mainContentEl) {
                mainContentEl.style.textAlign = 'center';
              }

              const dotEl = eventEl.querySelector('.fc-list-event-dot');
              if (dotEl) {
                dotEl.style.borderColor = color;
              }

              const titleEl = eventEl.querySelector('.fc-list-event-title');
              if (titleEl) {
                titleEl.style.textAlign = 'center';
              }

              // Configurar tooltip
              const tooltipContent = `
                <div style="text-align: left; font-size: 12px;">
                  <strong>Título:</strong> ${info.event.title}<br>
                  <strong>Tipo:</strong> ${info.event.extendedProps.type || ''}<br>
                  <strong>Responsable:</strong> ${info.event.extendedProps.name_admin || ''}<br>
                  <strong>Cliente:</strong> ${info.event.extendedProps.nombre_cliente || ''}<br>
                  <strong>Empresa:</strong> ${info.event.extendedProps.nombre_empresa || ''}<br>
                  <strong>Estado:</strong> ${info.event.extendedProps.estado_agenda || ''}<br>
                  <strong>Descripción:</strong> ${info.event.extendedProps.description || ''}
                </div>
              `;

              tippy(eventEl, {
                content: tooltipContent,
                allowHTML: true,
                theme: 'light',
                placement: 'top',
                interactive: true,
                maxWidth: 300
              });
            }
          }}
          eventContent={(eventInfo) => {
            return (
              <>
                {eventInfo.timeText && (
                  <div className="fc-event-time" style={{
                    fontWeight: 'bold',
                    wordBreak: 'break-word',
                    textAlign: 'center',
                    width: '100%'
                  }}>
                    {eventInfo.timeText}
                  </div>
                )}
                <div className="fc-event-title-container" style={{
                  textAlign: 'center',
                  width: '100%'
                }}>
                  <div className="fc-event-title" style={{
                    wordBreak: 'break-word',
                    marginTop: '2px',
                    marginBottom: '2px'
                  }}>
                    {eventInfo.event.title}
                  </div>
                  {eventInfo.event.extendedProps.name_admin && (
                    <div className="fc-event-admin" style={{
                      fontSize: '0.8em',
                      opacity: 0.9,
                      wordBreak: 'break-word'
                    }}>
                      {eventInfo.event.extendedProps.name_admin}
                    </div>
                  )}
                </div>
              </>
            );
          }}
          eventClassNames="fc-event-no-padding"
        />
      </div>
    </div>
  );
};

export default AgendaVer;
